/**
 * @fileoverview useLocalToday — the calendar day it is where the user sits.
 *
 * `new Date().toISOString()` gives the UTC day, which for an Australian
 * practitioner is yesterday until late morning. Anything keyed on "today"
 * for the user (the missing-timesheet count, day-scoped queries) must use
 * the local day, in the same `yyyy-MM-dd` form as `isoDate` in
 * `@/components/timesheets/weekGrid`. Long-lived surfaces such as the app
 * sidebar also need the value to roll over at local midnight without a
 * re-render from elsewhere, so the hook re-arms a timer for that moment.
 */

import { useEffect, useState } from "react";
import { isoDate } from "@/components/timesheets/weekGrid";

/** Milliseconds from `now` until the next local midnight. */
function msUntilNextLocalMidnight(now: Date): number {
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return Math.max(1, next.getTime() - now.getTime());
}

/** The user's local calendar day as `yyyy-MM-dd`, updated at local midnight. */
export function useLocalToday(): string {
  const [today, setToday] = useState(() => isoDate(new Date()));
  useEffect(() => {
    // Re-sync first: the initial state was read at render, and midnight may
    // have passed between then and the effect running (a no-op otherwise).
    const now = new Date();
    setToday(isoDate(now));
    const timer = setTimeout(() => {
      setToday(isoDate(new Date()));
    }, msUntilNextLocalMidnight(now));
    return () => clearTimeout(timer);
  }, [today]);
  return today;
}
